import type React from "react";
import {
  Badge,
  Box,
  Icon,
  Kbd,
  Progress,
  Text,
  type IconName,
} from "@salestrak/ui";
/**
 * App-side rich content for What's-New entries, keyed by the catalog entry's
 * `key`. The announcements catalog is data-only (it also renders on the
 * marketing site at build time and must stay free of runtime imports), so
 * anything interactive — a live demo, a mini-tour, extra actions — lives
 * here and renders inside the entry's row, below the markdown body.
 *
 * The marketing digest ignores this registry entirely — public entries render
 * only the catalog's markdown body and media.
 *
 * Vignettes below are illustrative mock UI (no real entity data), framed by
 * `VignetteFrame` so they read as figures, not live controls.
 */

function VignetteFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box surface="sunken" padding="md" rounded="md">
      {children}
    </Box>
  );
}

function StatTile({
  label,
  value,
  fillPct,
}: {
  label: string;
  value: string;
  fillPct: number;
}) {
  return (
    <Box
      surface="sunken"
      border
      direction="column"
      flex={1}
      gap="2xs"
      padding="sm"
      rounded="md"
    >
      <Text size="xs" truncate variant="muted">
        {label}
      </Text>
      <Text size="lg" tabular weight="semibold">
        {value}
      </Text>
      <Progress value={fillPct} />
    </Box>
  );
}

/** Mini dashboard: what a widget grid looks like, in two tiles. */
function WidgetsVignette() {
  return (
    <VignetteFrame>
      <Box direction="row" gap="sm">
        <StatTile fillPct={72} label="Pipeline" value="$182k" />
        <StatTile fillPct={45} label="Signed this week" value="14" />
      </Box>
    </VignetteFrame>
  );
}

/** A faux note line: mention chip + slash-command hint. */
function RicherNotesVignette() {
  return (
    <VignetteFrame>
      <Box direction="column" gap="xs">
        <Box surface="sunken" border padding="sm" rounded="md">
          <Text size="sm">
            Ridge cap replaced, photos attached —{" "}
            <Text size="sm" variant="primary" weight="semibold">
              @Install Crew
            </Text>{" "}
            please verify flashing.
          </Text>
        </Box>
        <Box align="center" direction="row" gap="xs">
          <Kbd>/</Kbd>
          <Text size="xs" variant="muted">
            for quick actions — attach, assign, checklist
          </Text>
        </Box>
      </Box>
    </VignetteFrame>
  );
}

/** Three permission rows with allow/deny marks. */
function PermissionsVignette() {
  const rows: Array<{ label: string; allowed: boolean }> = [
    { label: "View all workflows", allowed: true },
    { label: "Manage custom fields", allowed: true },
    { label: "Invite members", allowed: false },
  ];
  return (
    <VignetteFrame>
      <Box direction="column" gap="xs">
        {rows.map((row, index) => (
          <Box
            key={`${row.label}-${index}`}
            align="center"
            direction="row"
            gap="sm"
            justify="between"
          >
            <Box align="center" direction="row" gap="sm" shrink minWidth={0}>
              <Icon
                color={row.allowed ? "success" : "muted"}
                name={row.allowed ? "check" : "x"}
                size="sm"
              />
              <Text size="sm">{row.label}</Text>
            </Box>
            <Badge size="sm" variant={row.allowed ? "success" : "secondary"}>
              {row.allowed ? "Allowed" : "Admins only"}
            </Badge>
          </Box>
        ))}
      </Box>
    </VignetteFrame>
  );
}

/** The Module Rail in miniature — a row of tiles, one active. */
function NavigationVignette() {
  const tiles: Array<{ icon: IconName; active?: boolean }> = [
    { icon: "inbox" },
    { icon: "layout-grid", active: true },
    { icon: "map-pin" },
    { icon: "users" },
    { icon: "settings" },
  ];
  return (
    <VignetteFrame>
      <Box align="center" direction="row" gap="sm">
        {tiles.map((tile, index) => (
          <Box
            key={`${tile.icon}-${index}`}
            background={tile.active ? "selected" : "card"}
            border={!tile.active}
            padding="sm"
            rounded="md"
          >
            <Icon
              color={tile.active ? "primary" : "muted"}
              name={tile.icon}
              size="sm"
            />
          </Box>
        ))}
        <Text size="xs" variant="muted">
          Reorder and show/hide — synced to you
        </Text>
      </Box>
    </VignetteFrame>
  );
}

export const ANNOUNCEMENT_CONTENT: Record<string, React.ComponentType> = {
  widgets: WidgetsVignette,
  "richer-notes": RicherNotesVignette,
  "roles-and-permissions": PermissionsVignette,
  "redesigned-navigation": NavigationVignette,
};
